"use client";

import { useExperimentVariant } from "@sa-nu/experiments/experiment-context";
import {
  PlainDate,
  type PlainDateString,
  type ZonedDateTime,
  getCurrentTime,
  toPlainDateString,
} from "@sa-nu/shared-utils/date";
import {
  type Dispatch,
  type SetStateAction,
  useEffect,
  useRef,
  useState,
} from "react";

import type {
  AccommodationReservationProviderInput,
  StayTicketMasterReferenceInput,
} from "../../../../__generated__/gql/graphql";
import type { NoCancelOptionConfig } from "../../../../constants";
import { useGtm } from "../../../../lib/google-tag-manager/gtm";
import {
  useReservationEstimate,
  type UseReservationEstimateResult,
} from "../../_hooks/use-reservation-estimate";
import {
  DatePicker,
  type DatePickerSelection,
  type UnavailablePeriod,
} from "../../dx/ui/date-picker";
import { LoadingIndicator } from "../../dx/ui/loading-indicator";
import type { SelectedAccommodationReservation } from "../../reservation-form/reservation-calendar/reservation-calendar";
import { CtaButton } from "../../room-detail/content/cta-button";
import { DETAIL_DRAWER_BODY_HEIGHT_CLASS } from "../../room-detail/detail-drawer-layout";
import { computeSelectionCenterNight } from "../_utils/compute-selection-center-night";
import { getInitialVisibleDate } from "../_utils/get-initial-visible-date";
import { getSameDayMinNight } from "../_utils/reservation-cutoff";
import { resolveAccommodationForRoomType } from "../_utils/room-type-stay-availability";

import { ConfirmedPriceSummary } from "./_components/confirmed-price-summary";
import {
  FeeOptionCards,
  type FeeOptionCardData,
} from "./_components/fee-option-cards";
import {
  useConfirmedFeeOptions,
  type ConfirmedFeeOption,
} from "./_hooks/use-confirmed-fee-options";
import {
  useRoomTypeAvailability,
  useRoomTypeAvailabilityByStayTicketMaster,
  type RoomTypeAvailabilityResult,
} from "./_hooks/use-room-type-availability";
import { buildDateInfoMap } from "./_utils/build-date-info-map";
import { canUseGuaranteedPriceAsConfirmedAmountForStayTicketMaster } from "./_utils/can-use-guaranteed-price-as-confirmed-amount-for-stay-ticket-master";
import { computeScrollTopToAlignElementTop } from "./_utils/compute-scroll-top-to-align-element-top";
import { deriveConfirmedPrice } from "./_utils/derive-confirmed-price";
import { findScrollableAncestor } from "./_utils/find-scrollable-ancestor";
import { formatStayScheduleLabel } from "./_utils/format-stay-schedule-label";
import { getLeadTimeBoundaryDate } from "./_utils/get-lead-time-boundary-date";

/** 非 null のときは /ticket-issue の directConfirm 導線で、この StayTicketTemplate のチケットを発行して確定する */
export type RoomTypeReservationConfirmOptions = {
  stayTicketTemplateLookupKey: string | null;
};

type RoomTypeReservationConfirmHandler = (
  selection: SelectedAccommodationReservation,
  options: RoomTypeReservationConfirmOptions,
) => void;

type CommonProps = {
  roomTypeId: string;
  evChargerAvailable: boolean;
  onConfirm: RoomTypeReservationConfirmHandler;
  /** 指定時はこの日を初泊の初期選択としてカレンダーを開く */
  initialFirstNight?: PlainDate | null;
  /** 指定時はこの日を最終泊の初期選択としてカレンダーを開く */
  initialLastNight?: PlainDate | null;
  /** チェックイン日として選べる下限。先頭の空き日程・当日15時カットオフ・この値のうち最も遅い日を DatePicker の minDate として扱う */
  minNightFloor?: PlainDate | null;
  /** 滞在可能な最大泊数（StayTicket=束ねたチケット枚数）。DatePicker の泊数窓をこの値以下にクランプする */
  maxStayNights: number;
  /** 注記つきで表示する予約できない期間。未指定なら表示しない */
  unavailablePeriods?: readonly UnavailablePeriod[];
  currentTime?: ZonedDateTime;
};

type Props =
  | (CommonProps & {
      mode: "provider";
      provider: AccommodationReservationProviderInput;
      // RoomType.availabilities（provider版）は StayTicketMaster を解決しないため、
      // stayTicketMaster モードと異なり fetch 結果から導出できず呼び出し元から受け取る。
      /** 確定金額表示可否の判定に使うStayTicketMasterのID。取得できない場合は null */
      stayTicketMasterId?: string | null;
    })
  | (CommonProps & {
      mode: "stayTicketMaster";
      /** 予約可能資産・期間・料金計算条件の解決元となる StayTicketMaster の参照（base オプション） */
      stayTicketMasterReference: StayTicketMasterReferenceInput;
      /** 非 null のときのみ、CI がリードタイム境界日以降の日程で料金オプションカードを2枚出す */
      noCancelOptionConfig: NoCancelOptionConfig | null;
    });

// 2つの日付のうち遅い方。b が null/undefined のときは a。
function laterOf(a: PlainDate, b: PlainDate | null | undefined): PlainDate {
  return b != null && PlainDate.compare(b, a) > 0 ? b : a;
}

// DatePicker に重なる FeeOptionCards の最大高さ。2枚 + 見出し表示時の計算値約228px に、
// フォント描画差やシャドウ分の余裕を持たせた概算値。
const FEE_OPTION_CARDS_MAX_HEIGHT_PX = 270;

const BASE_CARD_LABEL = "予約の変更可能";
// 比較対象の変更不可カードが無いときは「変更可能」を強調する意味がない。
const BASE_ONLY_CARD_LABEL = "宿泊料金";
const NO_CANCEL_OPTION_CARD_LABEL = "変更不可";
const NO_CANCEL_OPTION_CARD_SUB_LABEL = "キャンセル料100%";

type DateRangeSelection = {
  selection: DatePickerSelection;
  setSelection: Dispatch<SetStateAction<DatePickerSelection>>;
  minSelectableNight: PlainDate;
  validInitialFirstNight: PlainDate | null;
  isConfirmed: boolean;
  confirmedFirstNight: PlainDate | null;
  confirmedLastNight: PlainDate | null;
  resolvedAccommodationId: string | null;
};

/**
 * CI/CO 選択状態とその派生値（確定判定・確定日程から通せる1室の解決）の管理。
 * availabilities の取得元（provider か stayTicketMaster か）に依存しないため、
 * mode 別コンポーネント間で共有できる（GraphQL fetch を伴わずスキップ分岐も無いため、
 * 複数コンポーネントから同じ順序で呼んでも Rules of Hooks に反しない）。
 */
function useDateRangeSelection({
  nights,
  initialFirstNight,
  initialLastNight,
  minNightFloor,
  currentTime,
}: {
  nights: RoomTypeAvailabilityResult["nights"];
  initialFirstNight?: PlainDate | null;
  initialLastNight?: PlainDate | null;
  minNightFloor?: PlainDate | null;
  currentTime: ZonedDateTime;
}): DateRangeSelection {
  // チェックイン日の下限 = 当日15時カットオフ と minNightFloor の遅い方（＝厳しい下限）。
  const minSelectableNight = laterOf(
    getSameDayMinNight(currentTime),
    minNightFloor,
  );

  // pre-fill された初泊日が下限より前 (15時以降に当日を引き継いだ等) なら選択済みにしない。
  // DatePicker は minDate 未満でも選択中 startDate を "selected" 描画するため、ここで弾く。
  const validInitialFirstNight =
    initialFirstNight != null &&
    PlainDate.compare(initialFirstNight, minSelectableNight) >= 0
      ? initialFirstNight
      : null;

  const [selection, setSelection] = useState<DatePickerSelection>(() => ({
    startDate: validInitialFirstNight,
    // CO 日 = lastNight + 1 (DatePicker は区間の翌日を endDate とする)
    endDate:
      validInitialFirstNight != null && initialLastNight != null
        ? initialLastNight.add({ days: 1 })
        : null,
  }));

  const isConfirmed =
    selection.startDate !== null && selection.endDate !== null;
  const confirmedFirstNight = isConfirmed ? selection.startDate : null;
  const confirmedLastNight =
    isConfirmed && selection.endDate !== null
      ? selection.endDate.subtract({ days: 1 })
      : null;

  // nights 未取得（空配列）や、選択範囲に共通の空室が無い場合は null。
  // Safe to call with empty nights — resolveAccommodationForRoomType returns null.
  const resolvedAccommodationId =
    confirmedFirstNight !== null && confirmedLastNight !== null
      ? resolveAccommodationForRoomType({
          nights,
          firstNight: confirmedFirstNight,
          lastNight: confirmedLastNight,
        })
      : null;

  return {
    selection,
    setSelection,
    minSelectableNight,
    validInitialFirstNight,
    isConfirmed,
    confirmedFirstNight,
    confirmedLastNight,
    resolvedAccommodationId,
  };
}

/** stayTicketMaster モードのときのみ渡す。未指定時は単一 CTA 導線を描画する */
type FeeOptionsProps = {
  baseTemplateLookupKey: string;
  /** null はキャンセル不可オプションを提供しない日程で、base 1枚だけを表示し lookupKey なしで確定する */
  confirmedFeeOptions: {
    base: ConfirmedFeeOption;
    noCancelOption:
      | (ConfirmedFeeOption & { config: NoCancelOptionConfig })
      | null;
  } | null;
};

type FeeOptionKind = "base" | "noCancelOption";

// GTM のパラメータ値は snake_case に揃える。
const FEE_OPTION_GTM_VALUE: Record<FeeOptionKind, string> = {
  base: "base",
  noCancelOption: "no_cancel_option",
};

type CalendarBodyProps = {
  roomTypeId: string;
  stayTicketMasterId?: string | null;
  onConfirm: RoomTypeReservationConfirmHandler;
  initialLastNight?: PlainDate | null;
  maxStayNights: number;
  unavailablePeriods?: readonly UnavailablePeriod[];
  currentTime: ZonedDateTime;
  availability: RoomTypeAvailabilityResult;
  dateSelection: DateRangeSelection;
} & (
  | { feeOptions: FeeOptionsProps; estimate?: never }
  // 見積り金額は provider モードの単一 CTA でしか表示しない。
  | { feeOptions?: undefined; estimate: UseReservationEstimateResult }
);

/**
 * 部屋タイプ予約のチェックイン・チェックアウト日選択カレンダー本体。
 * mode 別コンポーネントが取得した空き状況・料金を受け取って描画するだけの共有部分
 * （provider / stayTicketMaster のどちらの fetch hook も知らない）。
 *
 * NOTE(重複): reservation-form の CheckoutDatePickerContent と構造がほぼ同じ
 * （header / DatePicker / 見積り付き submit CTA / loading・error・empty）。
 * ただしデータ層は別物（こちらは部屋タイプ N 室から通せる 1 室を解決、あちらは単一
 * accommodation で CO のみ選択）。デザイン確定時に共通化を検討する。
 */
function CalendarBody(props: CalendarBodyProps) {
  const {
    roomTypeId,
    stayTicketMasterId = null,
    onConfirm,
    initialLastNight,
    maxStayNights,
    unavailablePeriods,
    currentTime,
    availability: { nights, facilitiesByAccommodationId, loading, error },
    dateSelection: {
      selection,
      setSelection,
      minSelectableNight,
      validInitialFirstNight,
      isConfirmed,
      confirmedFirstNight,
      confirmedLastNight,
      resolvedAccommodationId,
    },
    feeOptions,
  } = props;
  const canUseGuaranteedPriceAsConfirmedAmount =
    canUseGuaranteedPriceAsConfirmedAmountForStayTicketMaster(
      stayTicketMasterId,
    );

  const today = currentTime.toPlainDate();

  const gtm = useGtm();

  // フィルタ日程から初期日程が引き継がれている場合、開いた時点で「選択済み」として送信する。
  // CI/CO 両方が pre-fill されていれば両方送る（滞在期間指定で開いた時に checkin だけ飛んで
  // checkout が欠ける不整合を防ぐ）。カレンダーは開くたび再マウントするので実質「開いた時に一度」。
  // 送信済みフラグで、StrictMode の effect 二重実行や再レンダーによる多重発火を防ぐ。
  // validInitialFirstNight を見るのは、カットオフで弾かれた pre-fill を「選択済み」として
  // 誤送信しないため。
  const initialSelectionSent = useRef(false);
  useEffect(() => {
    if (initialSelectionSent.current || validInitialFirstNight == null) return;
    initialSelectionSent.current = true;
    gtm.sendEvent("rt_rsv_checkin_selected", { room_type_id: roomTypeId });
    if (initialLastNight != null) {
      gtm.sendEvent("rt_rsv_checkout_selected", { room_type_id: roomTypeId });
    }
  }, [validInitialFirstNight, initialLastNight, roomTypeId, gtm]);

  // CO 選択時に外側のスクロールコンテナを見出しまで動かす。DatePicker 内部のスクロールは
  // 別コンテナで外側の scrollTop に依存しないため、同時に走らせてよい。
  const headingRef = useRef<HTMLDivElement>(null);
  const previousCheckOutDateRef = useRef(selection.endDate);
  useEffect(() => {
    const previousCheckOutDate = previousCheckOutDateRef.current;
    previousCheckOutDateRef.current = selection.endDate;
    if (selection.endDate === null) return;
    if (previousCheckOutDate?.equals(selection.endDate) === true) return;
    const heading = headingRef.current;
    if (heading === null) return;
    const scrollableAncestor = findScrollableAncestor(heading);
    if (scrollableAncestor === null) return;
    scrollableAncestor.scrollTo({
      top: computeScrollTopToAlignElementTop(scrollableAncestor, heading),
      behavior: "smooth",
    });
  }, [selection.endDate]);

  if (loading) {
    return (
      <div
        className={`flex items-center justify-center pt-[24px] ${DETAIL_DRAWER_BODY_HEIGHT_CLASS}`}
      >
        <LoadingIndicator label="空き状況を読み込み中…" />
      </div>
    );
  }

  if (error) {
    return (
      <div
        className={`flex items-center justify-center px-[16px] pt-[24px] ${DETAIL_DRAWER_BODY_HEIGHT_CLASS}`}
      >
        <p className="font-notojp text-dx-text-sub text-[14px]">
          空き状況を取得できませんでした。時間をおいて再度お試しください。
        </p>
      </div>
    );
  }

  if (nights.length === 0) {
    return (
      <div
        className={`flex items-center justify-center px-[16px] pt-[24px] ${DETAIL_DRAWER_BODY_HEIGHT_CLASS}`}
      >
        <p className="font-notojp text-dx-text-sub text-[14px]">
          予約可能な日程がありません。
        </p>
      </div>
    );
  }

  const firstNightEntry = nights[0];
  const lastNightEntry = nights[nights.length - 1];
  // nights.length === 0 is already handled above; these are always defined
  if (firstNightEntry === undefined || lastNightEntry === undefined) {
    return null;
  }
  // 先頭の空き夜と上記の下限のうち遅い方を minDate にする
  const minDate = laterOf(firstNightEntry.night, minSelectableNight);
  const maxDate = lastNightEntry.night;

  // Upper bound for DatePicker range mode. CO date can be at most maxDate+1
  // (the day after the last available night), so the window from minDate to
  // maxDate+1 covers every valid checkout. Adding 1 ensures the furthest CO
  // cell is reachable by isValidEndDateCandidate.
  const windowNights = minDate.until(maxDate, { largestUnit: "day" }).days + 1;
  const maxNights = Math.min(windowNights, maxStayNights);

  const dateInfoMap = buildDateInfoMap({ nights, selection, minDate, maxDate });

  const nightPrices: Record<PlainDateString, number> = Object.fromEntries(
    nights.map((n) => [toPlainDateString(n.night), n.price]),
  );
  const totalPrice = deriveConfirmedPrice(
    canUseGuaranteedPriceAsConfirmedAmount,
    nightPrices,
    confirmedFirstNight,
    confirmedLastNight,
  );

  // 選択可能な上限。チェックインは maxDate(最終泊)まで。CO 選択中だけ +1 して maxDate+1 を
  // CO として選べるようにする。
  const selectableMaxDate =
    selection.startDate !== null && selection.endDate === null
      ? maxDate.add({ days: 1 })
      : maxDate;
  // 描画上限。最終泊が土曜だと CO 日(翌週日曜)が別週になり描画されないため、常に CO 日の週まで
  // 描画して CO セルを見せる。CO 確定後も選択済み表示が消えないようにする効果もある。
  const renderMaxDate = maxDate.add({ days: 1 });

  // handleChange は選択状態を更新するだけ。確定は明示的なフッターボタンで行う。
  const handleChange = (next: DatePickerSelection) => {
    const prevStart = selection.startDate;
    if (
      next.startDate !== null &&
      (prevStart === null || PlainDate.compare(prevStart, next.startDate) !== 0)
    ) {
      gtm.sendEvent("rt_rsv_checkin_selected", { room_type_id: roomTypeId });
    }
    if (next.endDate !== null && selection.endDate === null) {
      gtm.sendEvent("rt_rsv_checkout_selected", { room_type_id: roomTypeId });
    }
    setSelection(next);
  };

  // feeOption は料金オプションカードから確定したときのみ渡る（provider モードの単一 CTA では無い）。
  const handleSubmit = (feeOption?: {
    kind: FeeOptionKind;
    stayTicketTemplateLookupKey: string | null;
  }) => {
    if (
      !isConfirmed ||
      confirmedFirstNight === null ||
      confirmedLastNight === null ||
      resolvedAccommodationId === null
    )
      return;
    gtm.sendEvent("rt_rsv_calendar_reserve_clicked", {
      room_type_id: roomTypeId,
      ...(feeOption !== undefined && {
        fee_option: FEE_OPTION_GTM_VALUE[feeOption.kind],
      }),
    });
    onConfirm(
      {
        accommodationId: resolvedAccommodationId,
        firstNight: confirmedFirstNight,
        lastNight: confirmedLastNight,
        accommodationFacilities:
          facilitiesByAccommodationId.get(resolvedAccommodationId) ?? [],
      },
      {
        stayTicketTemplateLookupKey:
          feeOption?.stayTicketTemplateLookupKey ?? null,
      },
    );
  };

  // feeOptions を引数で受け取り、provider モードでは呼ばれないことを型で表す。
  function buildFeeOptionCards({
    baseTemplateLookupKey,
    confirmedFeeOptions,
  }: FeeOptionsProps): FeeOptionCardData[] | null {
    if (!isConfirmed) return null;

    if (confirmedFeeOptions === null) {
      return [
        {
          key: "base",
          label: BASE_ONLY_CARD_LABEL,
          subLabel: null,
          discountLabel: null,
          price: totalPrice,
          loading: false,
          // 確定金額を出せない StayTicketMaster でも予約自体は続行できるため、price で無効化しない。
          disabled: resolvedAccommodationId === null,
          onClick: () =>
            handleSubmit({ kind: "base", stayTicketTemplateLookupKey: null }),
        },
      ];
    }

    const baseCard: FeeOptionCardData = {
      key: "base",
      label: BASE_CARD_LABEL,
      subLabel: null,
      discountLabel: null,
      price: confirmedFeeOptions.base.price,
      loading: confirmedFeeOptions.base.loading,
      onClick: () =>
        handleSubmit({
          kind: "base",
          stayTicketTemplateLookupKey: baseTemplateLookupKey,
        }),
    };

    const { noCancelOption } = confirmedFeeOptions;
    if (noCancelOption === null) {
      return [{ ...baseCard, label: BASE_ONLY_CARD_LABEL }];
    }

    const noCancelOptionCard: FeeOptionCardData = {
      key: "noCancelOption",
      label: NO_CANCEL_OPTION_CARD_LABEL,
      subLabel: NO_CANCEL_OPTION_CARD_SUB_LABEL,
      discountLabel: noCancelOption.config.discountLabel,
      price: noCancelOption.price,
      loading: noCancelOption.loading,
      onClick: () =>
        handleSubmit({
          kind: "noCancelOption",
          stayTicketTemplateLookupKey:
            noCancelOption.config.stayTicketTemplateLookupKey,
        }),
    };

    return [baseCard, noCancelOptionCard];
  }

  const feeOptionCards =
    feeOptions !== undefined ? buildFeeOptionCards(feeOptions) : null;
  const scheduleLabel =
    confirmedFirstNight !== null && confirmedLastNight !== null
      ? formatStayScheduleLabel(
          confirmedFirstNight,
          confirmedLastNight.add({ days: 1 }),
        )
      : null;

  return (
    <div
      className={`bg-dx-surface-subtle flex flex-col pt-[24px] ${DETAIL_DRAWER_BODY_HEIGHT_CLASS}`}
    >
      {/* Instruction header */}
      <div ref={headingRef} className="shrink-0 px-[16px] pt-[16px] pb-[8px]">
        <p className="font-notojp text-dx-text-default text-[20px] leading-[1.4] font-semibold">
          日程を選択
        </p>
      </div>

      {/* Calendar body: 見出し・確定金額カードの残りをここに使わせる (min-h-0 flex-1)。
          DatePicker 自身が h-full + overflow-y-auto でスクロールコンテナになるため、
          この div は高さを配るだけでよい (自身はスクロールしない)。 */}
      <div className="min-h-0 flex-1">
        <DatePicker
          minDate={minDate}
          maxDate={selectableMaxDate}
          renderMaxDate={renderMaxDate}
          startDate={selection.startDate}
          endDate={selection.endDate}
          initialVisibleDate={getInitialVisibleDate(today)}
          onChange={handleChange}
          dateInfoMap={dateInfoMap}
          today={today}
          maxNights={maxNights}
          unavailablePeriods={unavailablePeriods}
          containScroll={false}
          background="subtle"
          centeredVisibleDate={
            confirmedFirstNight !== null && confirmedLastNight !== null
              ? computeSelectionCenterNight({
                  firstNight: confirmedFirstNight,
                  lastNight: confirmedLastNight,
                })
              : null
          }
          // 確定の瞬間に padding が変わると選択中セルの位置がずれるため、isConfirmed ではなく feeOptions の有無で決める。
          bottomPadding={
            feeOptions !== undefined ? FEE_OPTION_CARDS_MAX_HEIGHT_PX : 0
          }
        />
      </div>

      {/* feeOptions の分割代入では estimate が絞り込まれないため props で判定する */}
      {props.feeOptions === undefined ? (
        // provider モード専用の単一 CTA 導線。
        <>
          <ConfirmedPriceSummary totalPrice={totalPrice} />

          {/* Submit footer */}
          <CtaButton
            action={{
              kind: "proceed",
              label:
                totalPrice !== null ? "予約情報の入力に進む" : "予約に進む",
              // 確定金額を独立行で表示済みのため、ボタン内の金額表示は消す。
              subLabel:
                totalPrice !== null
                  ? undefined
                  : isConfirmed
                    ? "合計"
                    : undefined,
              priceLabel:
                totalPrice !== null
                  ? undefined
                  : (props.estimate.total ?? undefined),
              priceLoading:
                totalPrice !== null ? false : props.estimate.loading,
              disabled: !isConfirmed || resolvedAccommodationId === null,
              onAction: () => handleSubmit(),
            }}
          />
        </>
      ) : (
        feeOptionCards !== null && (
          <FeeOptionCards
            cards={feeOptionCards}
            scheduleLabel={scheduleLabel}
          />
        )
      )}
    </div>
  );
}

function ProviderRoomTypeReservationCalendar({
  roomTypeId,
  provider,
  stayTicketMasterId,
  evChargerAvailable,
  onConfirm,
  initialFirstNight,
  initialLastNight,
  minNightFloor,
  maxStayNights,
  unavailablePeriods,
  currentTime = getCurrentTime(),
}: Extract<Props, { mode: "provider" }>) {
  const availability = useRoomTypeAvailability({
    roomTypeId,
    provider,
    evChargerAvailable,
    currentTime,
  });
  const dateSelection = useDateRangeSelection({
    nights: availability.nights,
    initialFirstNight,
    initialLastNight,
    minNightFloor,
    currentTime,
  });
  const estimate = useReservationEstimate({
    accommodationId: dateSelection.resolvedAccommodationId ?? "",
    provider,
    firstNight: dateSelection.confirmedFirstNight,
    lastNight: dateSelection.confirmedLastNight,
  });

  return (
    <CalendarBody
      roomTypeId={roomTypeId}
      stayTicketMasterId={stayTicketMasterId}
      onConfirm={onConfirm}
      initialLastNight={initialLastNight}
      maxStayNights={maxStayNights}
      unavailablePeriods={unavailablePeriods}
      currentTime={currentTime}
      availability={availability}
      dateSelection={dateSelection}
      estimate={estimate}
    />
  );
}

function StayTicketMasterRoomTypeReservationCalendar({
  roomTypeId,
  stayTicketMasterReference,
  evChargerAvailable,
  noCancelOptionConfig,
  onConfirm,
  initialFirstNight,
  initialLastNight,
  minNightFloor,
  maxStayNights,
  unavailablePeriods,
  currentTime = getCurrentTime(),
}: Extract<Props, { mode: "stayTicketMaster" }>) {
  const availability = useRoomTypeAvailabilityByStayTicketMaster({
    roomTypeId,
    stayTicketMasterReference,
    evChargerAvailable,
    currentTime,
  });
  const dateSelection = useDateRangeSelection({
    nights: availability.nights,
    initialFirstNight,
    initialLastNight,
    minNightFloor,
    currentTime,
  });
  const { confirmedFirstNight } = dateSelection;
  const { isRemote } = availability;
  const noCancelOptionVariant = useExperimentVariant("no-cancel-option");
  // キャンセル不可オプションは直前予約による機会損失を避けるため、リードタイム境界日以降だけ提供する。
  // isRemote は nights と同じレスポンスで届くため、null の間は日程も確定できない。
  const offeredNoCancelOptionConfig =
    noCancelOptionVariant === "treatment" &&
    noCancelOptionConfig !== null &&
    confirmedFirstNight !== null &&
    isRemote !== null &&
    PlainDate.compare(
      confirmedFirstNight,
      getLeadTimeBoundaryDate(currentTime, isRemote),
    ) >= 0
      ? noCancelOptionConfig
      : null;

  const confirmedFeeOptions = useConfirmedFeeOptions({
    roomTypeId,
    baseStayTicketMasterReference: stayTicketMasterReference,
    noCancelOptionStayTicketMasterReference: {
      templateLookupKey:
        offeredNoCancelOptionConfig?.stayTicketTemplateLookupKey ?? "",
    },
    evChargerAvailable,
    currentTime,
    firstNight: dateSelection.confirmedFirstNight,
    lastNight: dateSelection.confirmedLastNight,
    skip: offeredNoCancelOptionConfig === null,
  });
  // hook は config を受け取らないため、確定金額にカード表示用の config を添える（両者は同じ条件で null になる）。
  const confirmedFeeOptionsWithConfig =
    confirmedFeeOptions === null || offeredNoCancelOptionConfig === null
      ? null
      : {
          base: confirmedFeeOptions.base,
          noCancelOption:
            confirmedFeeOptions.noCancelOption === null
              ? null
              : {
                  ...confirmedFeeOptions.noCancelOption,
                  config: offeredNoCancelOptionConfig,
                },
        };

  return (
    <CalendarBody
      roomTypeId={roomTypeId}
      // 呼び出し元 props ではなく fetch 結果由来。availabilitiesByStayTicketMaster は
      // resolveStayTicketMasterReference で解決済みの ID を返すため、呼び出し元が
      // StayTicketMaster の ID を知らなくても常に確定金額判定に使える。
      stayTicketMasterId={availability.stayTicketMasterId}
      onConfirm={onConfirm}
      initialLastNight={initialLastNight}
      maxStayNights={maxStayNights}
      unavailablePeriods={unavailablePeriods}
      currentTime={currentTime}
      availability={availability}
      dateSelection={dateSelection}
      feeOptions={{
        baseTemplateLookupKey: stayTicketMasterReference.templateLookupKey,
        confirmedFeeOptions: confirmedFeeOptionsWithConfig,
      }}
    />
  );
}

/**
 * 部屋タイプ予約のチェックイン・チェックアウト日選択カレンダー。
 * mode: "provider" は発行済み StayTicketUnit 等の provider から、mode: "stayTicketMaster" は
 * provider を経由せず固定の StayTicketMaster から、それぞれ空き状況・料金を取得する。
 * どちらの情報を渡すかは呼び出し元が決め、使わない方のダミー値を用意する必要はない。
 */
export function RoomTypeReservationCalendar(props: Props) {
  if (props.mode === "provider") {
    return <ProviderRoomTypeReservationCalendar {...props} />;
  }
  return <StayTicketMasterRoomTypeReservationCalendar {...props} />;
}
